import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { UploadCloud, CheckCircle2, Loader2, MessageCircle, ArrowRight, ShieldCheck, ImagePlus } from "lucide-react";
import { SHOP } from "../../storefront/StoreLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function StoreUploadRx() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [order, setOrder] = useState(null);

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) return setError("Photo 5MB se chhoti honi chahiye");
    setError("");
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setError("Prescription ki photo zaroori hai");
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: up } = await axios.post(`${API}/public/rx-upload`, fd);
      const { data: ord } = await axios.post(`${API}/online-orders`, {
        customer_name: name,
        phone,
        address,
        note: note || "Prescription order (photo attached)",
        items: [],
        prescription_path: up.path,
        source: "website",
      });
      setOrder(ord);
    } catch (err) {
      setError(err.response?.data?.detail || "Upload nahi hua — thodi der baad try karein");
    } finally {
      setBusy(false);
    }
  };

  if (order) {
    const waText = encodeURIComponent(`Namaste Trustmeds Pharma! Prescription order ${order.order_no} — ${name}, ${phone}`);
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center" data-testid="order-success-confirmation">
        <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-4" />
        <h1 className="font-display text-3xl font-extrabold text-slate-900">Prescription mil gaya!</h1>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed">
          Order No: <span className="font-mono-num font-bold text-emerald-800">{order.order_no}</span><br />
          Hamara pharmacist aapki prescription check karke <span className="font-semibold">{phone}</span> par call karega — medicines aur total confirm hoga, phir order ready hoga.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <a href={`https://wa.me/917428811670?text=${waText}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-semibold px-5 py-3 rounded-full text-sm">
            <MessageCircle className="w-4 h-4" /> WhatsApp par baat karein
          </a>
          <Link to="/store" className="inline-flex items-center gap-2 border border-slate-300 text-slate-700 font-semibold px-5 py-3 rounded-full text-sm hover:bg-slate-50">
            Home <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="upload-rx-page">
      <div className="text-center mb-8">
        <span className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-900 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" /> Licensed pharmacist verifies every Rx
        </span>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 mt-4">Upload Prescription</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
          Doctor ki parchi ka saaf photo upload karein — hum medicines ready karenge aur confirm karne ke liye aapko call karenge.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded-3xl p-6 sm:p-8 space-y-5">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="rx-pick-btn"
          className={`w-full border-2 border-dashed rounded-2xl p-6 text-center transition-colors ${preview ? "border-emerald-300 bg-emerald-50/50" : "border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/30"}`}>
          {preview ? (
            <img src={preview} alt="Prescription preview" className="max-h-56 mx-auto rounded-xl object-contain" data-testid="rx-preview" />
          ) : (
            <>
              <ImagePlus className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
              <div className="font-semibold text-slate-800 text-sm">Prescription ka photo chunein</div>
              <div className="text-xs text-slate-400 mt-1">JPG / PNG / WebP — max 5MB</div>
            </>
          )}
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="rx-file-input" onChange={pick} />

        <div className="grid sm:grid-cols-2 gap-3">
          <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Full Name *" data-testid="rx-name-input"
            className="h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
          <input required value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone (10 digit) *" data-testid="rx-phone-input" inputMode="numeric"
            className="h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        </div>
        <textarea required value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Delivery Address (Kashipur / nearby) *" rows={2} data-testid="rx-address-input"
          className="w-full px-4 py-3 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
        <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Koi note? (optional)" data-testid="rx-note-input"
          className="w-full h-11 px-4 rounded-xl border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />

        {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="rx-error">{error}</div>}

        <button type="submit" disabled={busy} data-testid="rx-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-[#064E3B] hover:bg-[#043B2C] disabled:opacity-60 text-white font-semibold py-3.5 rounded-full transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
          Send Prescription — Call Confirmation ke saath
        </button>
        <p className="text-[11px] text-slate-400 text-center">Scheduled (Rx) medicines sirf valid prescription ke saath di jaati hain.</p>
      </form>
    </div>
  );
}
